<script setup>
import { useI18n } from 'vue-i18n';

import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StudioBadge from './StudioBadge.vue';

const props = defineProps({
  records: {
    type: Array,
    required: true,
  },
  matchType: {
    type: String,
    required: true,
    validator: value => ['exact', 'keyword'].includes(value),
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle']);

const { t } = useI18n();

const termLabel = record =>
  props.matchType === 'exact' ? record.question : record.keyword;
</script>

<template>
  <div
    class="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
  >
    <table
      class="w-full text-left border-collapse table-fixed"
      data-testid="answers-table"
    >
      <thead class="bg-n-alpha-1">
        <tr class="border-b border-n-weak">
          <th class="w-1/4 px-3 py-2 text-label-small text-n-slate-11">
            {{
              matchType === 'exact'
                ? $t('AI_AGENT.ANSWERS.TABLE.QUESTION')
                : $t('AI_AGENT.ANSWERS.TABLE.KEYWORD')
            }}
          </th>
          <th class="px-3 py-2 w-1/3 text-label-small text-n-slate-11">
            {{ $t('AI_AGENT.ANSWERS.TABLE.ANSWER') }}
          </th>
          <th class="w-20 px-3 py-2 text-label-small text-n-slate-11">
            {{ $t('AI_AGENT.ANSWERS.TABLE.PRIORITY') }}
          </th>
          <th class="w-20 px-3 py-2 text-label-small text-n-slate-11">
            {{ $t('AI_AGENT.ANSWERS.TABLE.HITS') }}
          </th>
          <th class="w-20 px-3 py-2 text-label-small text-n-slate-11">
            {{ $t('AI_AGENT.ANSWERS.TABLE.ENABLED') }}
          </th>
          <th class="w-24 px-3 py-2" />
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr
          v-for="record in records"
          :key="record.id"
          class="transition-colors group hover:bg-n-alpha-1 motion-reduce:transition-none"
          :class="{ 'opacity-60': !record.enabled }"
          :data-testid="`answer-row-${record.id}`"
        >
          <td class="px-3 py-3 align-top">
            <span class="block truncate text-body-main text-n-slate-12">
              {{ termLabel(record) }}
            </span>
            <div
              v-if="matchType === 'keyword' && record.synonyms?.length"
              class="flex flex-wrap gap-1 pt-1.5"
            >
              <StudioBadge v-for="synonym in record.synonyms" :key="synonym">
                {{ synonym }}
              </StudioBadge>
            </div>
          </td>
          <td class="px-3 py-3 align-top">
            <span class="line-clamp-2 text-body-main text-n-slate-11">
              {{ record.answer }}
            </span>
          </td>
          <td class="px-3 py-3 align-top">
            <StudioBadge>{{ record.priority }}</StudioBadge>
          </td>
          <td class="px-3 py-3 align-top">
            <StudioBadge :tone="record.hit_count ? 'blue' : 'slate'">
              <span class="i-lucide-zap size-3" />
              {{ record.hit_count }}
            </StudioBadge>
          </td>
          <td class="px-3 py-3 align-top">
            <Switch
              :model-value="record.enabled"
              :data-testid="`answer-toggle-${record.id}`"
              @update:model-value="emit('toggle', record)"
            />
          </td>
          <td class="px-3 py-2.5 align-top">
            <div
              class="flex items-center justify-end gap-1 transition-opacity opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 motion-reduce:transition-none"
            >
              <Button
                v-tooltip="t('AI_AGENT.ANSWERS.TABLE.EDIT')"
                icon="i-lucide-pencil"
                slate
                ghost
                xs
                :data-testid="`answer-edit-${record.id}`"
                @click="emit('edit', record)"
              />
              <Button
                v-tooltip="t('AI_AGENT.ANSWERS.TABLE.DELETE')"
                icon="i-woot-bin"
                slate
                ghost
                xs
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                :data-testid="`answer-delete-${record.id}`"
                @click="emit('delete', record)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
